import { HighlightCta, Typography } from '@americastestkitchen/mise';
import TasteTestButton from 'components/elements/QuickViewButton/components/TasteTestButton/TasteTestButton';
import BookQueryContext from 'components/providers/BookQueryContext';
import IngredientSelector from 'components/svgs/Recipe/Ingredients/IngredientSelector';
import { decode } from 'he';
import { useCallback, useContext, useState } from 'react';
import {
  RecipeIngredientItem,
  RecipeIngredientReviewSet,
} from 'types/api/recipe.types';
import { PrintFontOptions } from 'types/recipe';
import { getOpenPrintSize } from 'utils/getPrintSize';
import utils from '../../../../../../lib/utils';
import recipeUtils from '../../../../../../lib/utils/recipe';
import reviewSetUtils from '../../../../../../lib/utils/reviewSet';
import styles from './RecipeIngredient.module.scss';

type RecipeIngredientProps = {
  viewContext: string;
  recipeIngredientItem: RecipeIngredientItem;
  disabled?: boolean;
  printSize?: PrintFontOptions;
};

type IngredientText = {
  qty: string;
  preText: string;
  title: string;
  postText: string;
  printSize?: PrintFontOptions;
};

type ClickableIngredientText = {
  recipeIngredientItem: RecipeIngredientItem;
  title: string;
  relatedDoc: RecipeIngredientItem['fields']['document'];
  qty: string;
  preText: string;
  postText: string;
  printSize?: PrintFontOptions;
};

const formatQuantity = (quantity: string) => `${decode(quantity).trim()} `;

const getReviewSetData = (reviewSet: RecipeIngredientReviewSet) => {
  const {
    fields: { documentType, productReference: productRef, slug, title },
  } = reviewSet;
  const documentReference = { documentType, slug, title };
  const productReference = productRef ? { ...productRef.fields } : null;
  return reviewSetUtils.getReviewableData(
    { fields: { documentReference, productReference } },
    { displayPrice: false, siteKey: 'atk', tag: `atkingredientqv-20` },
  );
};

const Quanity = ({
  text,
  printSize,
}: {
  text: string;
  printSize?: PrintFontOptions;
}) => {
  return (
    <Typography
      type="open"
      size={printSize ? getOpenPrintSize(printSize) : 'lg'}
      weight="bold"
    >
      {formatQuantity(text)}
    </Typography>
  );
};

const TextTitle: React.FC<IngredientText> = ({
  qty,
  preText,
  title,
  postText,
  printSize,
}) => {
  return (
    <Typography
      type="open"
      size={printSize ? getOpenPrintSize(printSize) : 'lg'}
      weight="normal"
    >
      {qty && <Quanity text={qty} printSize={printSize} />}
      {`${preText} `}
      {title}
      {postText && ` ${postText}`}
    </Typography>
  );
};

const Title: React.FC<ClickableIngredientText> = ({
  recipeIngredientItem,
  title,
  relatedDoc,
  qty,
  preText,
  postText,
  printSize,
}) => {
  const bookQueries = useContext(BookQueryContext);
  const { reviewSet } = recipeIngredientItem.fields.ingredient.fields;

  let reviewable: object | undefined = undefined;

  if (reviewSet && reviewSet.fields.documentType === 'taste_test') {
    const reviewSetData = getReviewSetData(reviewSet);
    reviewable = reviewSetData;
  }

  const MainItem = useCallback(() => {
    if (reviewable && bookQueries) {
      return (
        bookQueries && (
          <>
            <TasteTestButton text={title} reviewable={reviewable} />
          </>
        )
      );
    } else if (relatedDoc) {
      const { documentType, slug } = relatedDoc.fields;
      return (
        <HighlightCta
          as="a"
          variant="secondary"
          displayType="inline"
          typography={{
            type: 'open',
            size: printSize ? getOpenPrintSize(printSize) : 'lg',
            weight: 'normal',
          }}
          href={`/${utils.docTypeToPath[documentType]}/${slug}`}
          rel="noopener noreferrer"
          target="_blank"
          className={styles.highlight}
        >
          {title}
        </HighlightCta>
      );
    } else {
      return (
        <Typography
          type="open"
          size={printSize ? getOpenPrintSize(printSize) : 'lg'}
          weight="normal"
        >
          {title}
        </Typography>
      );
    }
  }, [bookQueries, printSize, relatedDoc, reviewable, title]);

  return reviewable || relatedDoc ? (
    <Typography
      type="open"
      size={printSize ? getOpenPrintSize(printSize) : 'lg'}
      weight="normal"
      ariaHidden
    >
      {qty && <Quanity text={qty} printSize={printSize} />}
      {`${preText} `}
      <MainItem />
      {postText.charAt(0) !== ',' ? ` ${postText}` : postText}
    </Typography>
  ) : (
    <TextTitle
      qty={qty}
      preText={preText}
      title={title}
      postText={postText}
      printSize={printSize}
    />
  );
};

const RecipeIngredient: React.FC<RecipeIngredientProps> = ({
  viewContext,
  recipeIngredientItem,
  disabled = false,
  printSize,
}) => {
  const relatedDoc = recipeIngredientItem.fields.document;
  const { preText, postText, qty, title } = recipeUtils.ingredientItemData(
    recipeIngredientItem,
    relatedDoc,
  );
  const [isSelected, setIsSelected] = useState(false);

  return (
    <>
      {viewContext === 'quick-view' ? (
        <label
          className={styles.fakeCheckbox}
          htmlFor={`ingredient-${viewContext}-${recipeIngredientItem.id}`}
        >
          <TextTitle
            qty={qty}
            preText={preText}
            title={title}
            postText={postText}
          />
        </label>
      ) : (
        <div
          className={styles.controlCheckbox}
          onClick={() => setIsSelected(!isSelected)}
        >
          <input
            disabled={disabled}
            id={`ingredient-${viewContext}-${recipeIngredientItem.id}`}
            name={`${recipeIngredientItem.id}`}
            type="checkbox"
          />
          <span className={styles.checkbox}>
            <IngredientSelector isSelected={isSelected} />
          </span>
          <label
            htmlFor={`ingredient-${viewContext}-${recipeIngredientItem.id}`}
          >
            <Title
              recipeIngredientItem={recipeIngredientItem}
              title={title}
              relatedDoc={relatedDoc}
              qty={qty}
              preText={preText}
              postText={postText}
              printSize={printSize}
            />
          </label>
        </div>
      )}
    </>
  );
};

export default RecipeIngredient;
